import React, { useMemo, useRef } from "react"
import { Line } from "@nivo/line"
import { ResponsiveWrapper } from "@nivo/core"
import type { TableModel } from "utils/tableModel"
import type { LineTile as LineTileDoc } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import {
  chartCellLeaves,
  modelToLineSeries,
} from "features/Dashboard/runtime/modelAdapters"
import { effectiveStatistic } from "features/Dashboard/runtime/tileView"
import {
  bottomLegends,
  categoryTicks,
  CHART_SURFACE,
  chartUnits,
  entityColours,
  formatTick,
  formatValue,
  legendHeight,
  nivoTheme,
  prefersReducedMotion,
  RIGHT_LEGEND,
  rightLegends,
  targetMarker,
  truncateLabel,
  valueDomain,
} from "./chartTheme"
import type { LegendItem } from "./chartTheme"
import ChartTooltip from "./ChartTooltip.component"
import ChartFrame from "./ChartFrame.component"
import { useTileTrace } from "./useTileTrace"
import type { TraceTarget } from "./useTileTrace"
import { markId, splitIndex, useChartTraceMenu } from "./useChartTraceMenu"

type LineSeriesData = { id: string; data: { x: string; y: number | null }[] }

/** A point of a line: its series and its x category. */
type PointRef = { seriesId: string; x: string }

/**
 * The point nearest `y` (px down the plot) among `points`, the points of
 * one x position: the one a pointer at that height is on.
 */
export const nearestPoint = (
  points: readonly { seriesId: string | number; y: number; data: { x: unknown } }[],
  y: number
): PointRef | null => {
  const near = points.reduce<(typeof points)[number] | null>(
    (best, p) => (!best || Math.abs(p.y - y) < Math.abs(best.y - y) ? p : best),
    null
  )
  return near && { seriesId: String(near.seriesId), x: String(near.data.x) }
}

/** Legend key for a line series: a short stroke, as the mark is a line. */
const LineKey: React.FC<{ x: number; y: number; size: number; fill: string }> = ({
  x,
  y,
  size,
  fill,
}) => <rect x={x} y={y + size / 2 - 1} width={size} height={2} rx={1} fill={fill} />

/**
 * A line tile: the table's columns (the waves) along x, one line per row
 * category; with several column variables, the wave variable's categories
 * only. Like a bar chart it draws one unit, its first line's, and names
 * the rows it left out under the plot. The chart options add value labels
 * on the points, a dashed target
 * line, fixed axis ends and the legend's place. Points are not clickable;
 * a wave is not a filter people expect. Hovering shows every series at
 * that wave. In both modes a right-click offers the trace of the number of
 * the line nearest the pointer at that wave (or, with points shown, the
 * context menu key on a focused point). `compact` draws one panel of a
 * repeated tile: no legend (the panels share one), tighter margins, smaller
 * points, and the value axis up to `axisMax` (plotted units), the end every
 * panel shares; `panel` names its category.
 */
const LineTile: React.FC<{
  tile: LineTileDoc
  model: TableModel
  /** The tile's last result, which the colours follow (the view by default). */
  raw?: TableModel
  compact?: boolean
  axisMax?: number
  panel?: string
}> = ({ tile, model, raw = model, compact = false, axisMax, panel }) => {
  const palette = useDashboardStore((s) => s.document.theme.chartPalette)
  const colourMap = useDashboardStore(
    (s) => s.document.theme.categoryColours
  )
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const v = tile.visual
  const chart = tile.chart
  const statistic = effectiveStatistic(tile, controls)
  const { openTrace, panel: tracePanel } = useTileTrace(tile, panel)
  const { openMenu, menu } = useChartTraceMenu(openTrace)
  // The point under the pointer, read as it moves over the wave slices.
  const hovered = useRef<PointRef | null>(null)
  const series = useMemo(
    () =>
      modelToLineSeries(model, {
        statistic,
        hiddenSeries: v.hiddenSeries,
        waveVariable: v.waveVariable,
      }),
    [model, statistic, v.hiddenSeries, v.waveVariable]
  )
  const { measures, categories } = tile
  const colourOf = useMemo(
    () =>
      entityColours(raw, "down", palette, colourMap, {
        measures,
        labels: categories.labels,
      }),
    [raw, palette, colourMap, measures, categories.labels]
  )

  if (series.categories.length === 0 || series.series.length === 0) {
    return <p className="dq-tile__empty">No values to chart.</p>
  }

  const xLabel = new Map(series.categories.map((c) => [c.id, c.label]))
  const labelOfX = (x: unknown) => xLabel.get(String(x)) ?? String(x)
  const seriesLabel = new Map(series.series.map((s) => [s.id, s.label]))
  const data: LineSeriesData[] = series.series.map((s) => ({
    id: s.id,
    data: series.categories.map((c, i) => ({ x: c.id, y: s.values[i] })),
  }))
  const legendItems: LegendItem[] = series.series.map((s) => ({
    id: s.id,
    label: s.label,
    color: colourOf(s.id),
  }))
  const multi = data.length > 1
  const format = series.format
  const dataLabels = chart.dataLabels === "on"
  const legendAt = multi && !compact ? chart.legend : "none"
  const domain = valueDomain(
    series.series.flatMap((s) => s.values),
    chart,
    format,
    axisMax
  )
  const target = chart.target
  const markers = target
    ? [
        targetMarker(
          "y",
          chartUnits(target.value, format),
          target.label ||
            `Target ${formatValue(chartUnits(target.value, format), format)}`
        ),
      ]
    : []

  /**
   * The number of the point the event is on: a focused point (Nivo ids it
   * "<series>.<n>", n counting the series' drawn points), else the point
   * last hovered.
   */
  const traceTarget = (event: React.MouseEvent): TraceTarget | null => {
    const id = markId(event, "line.point.")
    let point = hovered.current
    if (id !== null) {
      const [seriesId, n] = splitIndex(id)
      const values = series.series.find((s) => s.id === seriesId)?.values ?? []
      const drawn = values.flatMap((v, i) => (v === null ? [] : [i]))
      const x = series.categories[drawn[n]]?.id
      point = x === undefined ? null : { seriesId, x }
    }
    if (!point) return null
    const { seriesId, x } = point
    const ci = series.categories.findIndex((c) => c.id === x)
    const value = series.series.find((s) => s.id === seriesId)?.values[ci]
    const leaves = chartCellLeaves(model, {
      categoryAxis: "across",
      categoryId: x,
      seriesId,
      statistic,
    })
    if (value === null || value === undefined || !leaves) return null
    return { ...leaves, value: formatValue(value, format) }
  }

  return (
    <ChartFrame
      kind="line"
      omitted={compact ? [] : series.omitted}
      onContextMenu={(e) => openMenu(e, traceTarget(e))}
      trace={
        <>
          {menu}
          {tracePanel}
        </>
      }
    >
      <ResponsiveWrapper>
        {({ width, height }) => {
          const legend =
            legendAt === "bottom" ? legendHeight(width, legendItems) : 0
          const right = legendAt === "right" ? RIGHT_LEGEND : 0
          const fitted = categoryTicks(
            width - (compact ? 52 : 72) - right,
            series.categories.length
          )
          const rotated = fitted.rotation !== 0
          const ticks =
            compact && rotated ? { ...fitted, maxChars: 10 } : fitted
          const margin = compact
            ? {
                top: dataLabels ? 18 : 6,
                right: 10,
                bottom: rotated ? 52 : 22,
                left: 40,
              }
            : {
                top: dataLabels ? 22 : 12,
                right: 20 + right,
                bottom: (rotated ? 72 : 28) + legend,
                left: 52,
              }
          return (
            <Line<LineSeriesData>
              width={width}
              height={height}
              data={data}
              theme={nivoTheme}
              colors={(s) => colourOf(String(s.id))}
              lineWidth={2}
              enablePoints={v.showPoints}
              pointSize={compact ? 7 : 10}
              pointBorderWidth={2}
              pointBorderColor={CHART_SURFACE}
              margin={margin}
              yScale={{
                type: "linear",
                min: domain.min,
                max: domain.max,
                nice: domain.nice,
              }}
              yFormat={(n) => formatValue(n, format)}
              enablePointLabel={dataLabels}
              pointLabel={(p) => formatValue(p.data.y as number, format)}
              pointLabelYOffset={-12}
              markers={markers}
              enableGridX={false}
              gridYValues={compact ? 4 : 5}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                tickValues: compact ? 4 : 5,
                format: (n) => formatTick(Number(n), format),
              }}
              axisBottom={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: ticks.rotation,
                format: (x) => truncateLabel(labelOfX(x), ticks.maxChars),
              }}
              enableSlices="x"
              onMouseMove={(datum, event) => {
                if (!("points" in datum)) return
                // A slice spans the plot's height from its top.
                const top = event.currentTarget.getBoundingClientRect().top
                hovered.current = nearestPoint(datum.points, event.clientY - top)
              }}
              onMouseLeave={() => {
                hovered.current = null
              }}
              isFocusable={v.showPoints}
              sliceTooltip={({ slice }) => (
                <ChartTooltip
                  title={labelOfX(slice.points[0]?.data.x)}
                  rows={slice.points.map((p) => ({
                    key: p.id,
                    colour: p.seriesColor,
                    value: p.data.yFormatted,
                    label: multi ? seriesLabel.get(p.seriesId) : undefined,
                  }))}
                />
              )}
              legends={
                legendAt === "bottom"
                  ? bottomLegends(width, legendItems, margin.bottom, LineKey)
                  : legendAt === "right"
                    ? rightLegends(legendItems, LineKey)
                    : []
              }
              animate={!prefersReducedMotion()}
              role="img"
              ariaLabel={tile.title ?? "Line chart"}
            />
          )
        }}
      </ResponsiveWrapper>
    </ChartFrame>
  )
}

export default LineTile
